import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { LoadingIndicatorLinear } from '../components/indicators/LoadingIndicatorLinear';
import LightDarkProvider from '../components/LightDarkProvider';
import AirportList from '../features/airport-list/AirportList';
import AirportSummary from '../features/airport-summary/AirportSummary';
import { CustomerAdmin } from '../features/customer-admin/CustomerAdmin';
import Dashboard from '../features/dashboard/Dashboard';
import { FlightList } from '../features/flight-list/FlightList';
import Home from '../features/home/Home';
import Login from '../features/login/Login';
import { LoginFrame } from '../features/login/LoginFrame';
import { RemoteLogin } from '../features/login/RemoteLogin';
import { RemoteLoginCallback } from '../features/login/RemoteLoginCallback';
import MultiWindowHost from '../features/multi-window/MultiWindowHost';
import { Reports } from '../features/reports/Reports';
import { settingsRoutes } from '../features/settings/router/settings-router';
import { SystemHub } from '../features/system-hub/SystemHub';
import { useAppSelector } from '../hooks/store-hooks';
import ApplicationRoute from './ApplicationRoute';
import AuthenticatedOnlyTemplate from './AuthenticatedOnlyTemplate';
import { RootErrorBoundary } from './error-boundaries/RootErrorBoundary';
import { MultiTabGuard } from './MultiTabGuard';
import { RequiresAuthenticationState } from './RequiresAuthenticationState';
import { ROUTES } from './routes';
import UnauthenticatedOnlyTemplate from './UnauthenticatedOnlyTemplate';

export const router = createBrowserRouter([
    {
        element: (
            <LightDarkProvider>
                <Outlet />
            </LightDarkProvider>
        ),
        errorElement: <RootErrorBoundary />,
        children: [
            {
                element: (
                    <MultiTabGuard>
                        <Outlet />
                    </MultiTabGuard>
                ),
                children: [
                    {
                        // Main application routes
                        element: (
                            <RequiresAuthenticationState
                                loader={
                                    <div>
                                        <LoadingIndicatorLinear />
                                        {import.meta.env.DEV && <div>Getting account information</div>}
                                    </div>
                                }>
                                <AuthenticatedOnlyTemplate redirectTo={ROUTES.LOGIN}>
                                    <ApplicationRoute>
                                        <Outlet />
                                    </ApplicationRoute>
                                </AuthenticatedOnlyTemplate>
                            </RequiresAuthenticationState>
                        ),
                        children: [
                            {
                                index: true,
                                element: <Home />
                            },
                            {
                                path: ROUTES.FLIGHT_LIST,
                                element: <FlightList />
                            },
                            {
                                path: ROUTES.AIRPORT_LIST,
                                element: <AirportList />
                            },
                            {
                                path: ROUTES.AIRPORT_SUMMARY,
                                element: <AirportSummary />
                            },
                            {
                                path: ROUTES.REPORTS,
                                element: <Reports />
                            },
                            {
                                path: ROUTES.SYSTEM_HUB,
                                element: <SystemHub />
                            },
                            {
                                path: ROUTES.CUSTOMER_ADMIN,

                                element: <CustomerAdminGuard />,
                                children: [{ index: true, element: <CustomerAdmin /> }]
                            },
                            {
                                path: ROUTES.DASHBOARD,
                                element: <DashboardGuard />,
                                children: [{ index: true, element: <Dashboard /> }]
                            },
                            ...settingsRoutes,
                            {
                                path: '*',
                                element: <Navigate to={ROUTES.HOME} />
                            }
                        ]
                    },
                    {
                        // Routes with login template
                        element: (
                            <LoginFrame>
                                <RequiresAuthenticationState
                                    loader={
                                        <div>
                                            <LoadingIndicatorLinear />
                                            {import.meta.env.DEV && <div>Getting account information</div>}
                                        </div>
                                    }>
                                    <Outlet />
                                </RequiresAuthenticationState>
                            </LoginFrame>
                        ),
                        children: [
                            {
                                path: ROUTES.LOGIN,
                                element: (
                                    <UnauthenticatedOnlyTemplate redirectTo={ROUTES.HOME}>
                                        <Login />
                                    </UnauthenticatedOnlyTemplate>
                                )
                            },
                            {
                                path: ROUTES.REMOTE_LOGIN,
                                element: <RemoteLogin />
                            },
                            {
                                path: ROUTES.REMOTE_LOGIN_CALLBACK,
                                element: <RemoteLoginCallback />
                            }
                        ]
                    }
                ]
            },
            // multi window does not require auth state because it serves just as a simple window portal
            {
                path: ROUTES.MULTI_WINDOW,
                element: <MultiWindowHost />
            }
        ]
    }
]);

function CustomerAdminGuard() {
    const canAccess = useAppSelector((x) => x.user.rights.customerAdmin);
    if (!canAccess) {
        return <Navigate to={'/'} />;
    }

    return <Outlet />;
}

function DashboardGuard() {
    const canAccess = useAppSelector((x) => x.user.rights.dashboard);
    if (!canAccess) {
        return <Navigate to={ROUTES.HOME} replace />;
    }

    return <Outlet />;
}
